import React, { useEffect, useLayoutEffect, useState } from "react";
import * as am4charts from "@amcharts/amcharts4/charts";
import { groupByTypes } from "../../../Findings/FindingsUtils";
import Chart from "../../../Components/Chart/Chart";
import useChart from "../../../Components/Chart/useChart";
import useSummary from "../../useSummary";
import useSeverityAsPriority, {
  SeverityFieldsEnum,
} from "../../../General/useSeverityAsPriority";
import { capitalize } from "lodash-es";
import useWidgetFilterNavigator from "../../useWidgetFilterNavigator";
import {
  createCategoryAxis,
  createSeries,
  createValueAxis,
  type SeriesHitEvent,
} from "../BarChartUtils";
import type { SelectedScope } from "../../../types/scope";

export interface AverageAgeBySeverityProps {
  selectedScope?: SelectedScope | null;
  filterToExecute?: string | null;
  scopeToExecute?: string | null;
  id: string | number;
  isForExport?: boolean;
  setIsReadyToExport?: (isReady: boolean) => void;
}

// A single bar datum, derived from each summary row.
interface SeverityChartDatum {
  severity: string;
  age: number;
  fill: string;
}

// Raw summary rows: [severity, age] tuples returned by useSummary.
type SummaryRow = [string, number];

const AverageAgeBySeverity: React.FC<AverageAgeBySeverityProps> = ({
  selectedScope,
  filterToExecute,
  scopeToExecute,
  id,
  isForExport,
  setIsReadyToExport,
}) => {
  const { severityDisplay, getSeverityField } = useSeverityAsPriority();
  const chartName = `AvgAgeBySeverityDashboard-${id}`;

  const { onFilterClick } = useWidgetFilterNavigator({
    filterToExecute: filterToExecute,
    filterId: null,
    scopeToExecute: scopeToExecute,
    scopeId: selectedScope?.value?.id,
  });

  const {
    summaryData: data,
    summaryDataError,
    summaryLoading,
    refetchSummary,
  } = useSummary({
    variables: {
      name: "FINDINGS_VIEW",
      selection: ["finding.age"],
      group_by: [`${groupByTypes.SEVERITY}`],
      aggregate_functions: ["AVG"],
      round_functions: ["ROUND"],
      filters_config: {
        filtersjson: filterToExecute,
        scopesjson: scopeToExecute || null,
        scopesid: selectedScope?.value?.id,
      },
    },
    skip: (!scopeToExecute && !selectedScope?.value?.id) || !filterToExecute,
  });
  const [dashboardData, setDashboardData] = useState<
    SeverityChartDatum[] | null
  >(null);
  const {
    chartLoading,
    dataError,
    dataExists,
    setChartError,
    setChartDataExists,
    startLoadingChart,
    stopLoadingChart,
    createChart,
  } = useChart(setIsReadyToExport ?? null);

  const clearData = () => {
    setDashboardData(null);
  };

  useEffect(() => {
    if (!!summaryLoading) {
      startLoadingChart();
      clearData();
      setChartError(false);
      setChartDataExists(false);
    }
  }, [summaryLoading, setChartDataExists, setChartError, startLoadingChart]);

  useEffect(() => {
    if (!!summaryDataError) {
      setChartError(true);
      stopLoadingChart();
    }
  }, [summaryDataError, setChartError, stopLoadingChart]);

  useEffect(() => {
    if (!!data) {
      // `data` is typed as unknown by useSummary; this widget consumes it as an
      // array of [severity, age] tuples.
      const rows = data as SummaryRow[];
      const dataArray: SeverityChartDatum[] = rows.map((el) => {
        return {
          severity: el[0],
          age: el[1],
          fill: getSeverityField(el[0], SeverityFieldsEnum.COLOR),
        };
      });

      if (dataArray.length === 0) {
        stopLoadingChart();
        setChartDataExists(false);
      } else if (dataArray.length) {
        setChartError(false);
        stopLoadingChart();
        setChartDataExists(true);
      }

      setDashboardData(dataArray);
    }
  }, [data]);

  useLayoutEffect(() => {
    if (dataExists) {
      stopLoadingChart();

      let [chart] = createChart(chartName, am4charts.XYChart, {
        hideLegend: true,
      });
      chart.data = dashboardData!;
      chart.paddingTop = 45;
      chart.paddingRight = 40;

      let categoryAxis = chart.yAxes.push(new am4charts.CategoryAxis());
      createCategoryAxis(
        categoryAxis,
        severityDisplay,
        "severity",
        false,
        (text) => capitalize(getSeverityField(text, SeverityFieldsEnum.NAME)),
        false
      );
      let valueAxis = chart.xAxes.push(new am4charts.ValueAxis());
      createValueAxis(valueAxis, "Average Age");
      createSeries(
        chart,
        false,
        "severity",
        (e: SeriesHitEvent) =>
          onFilterClick([
            [
              "in",
              "remediation_item.severity",
              (e.target.dataItem.dataContext as unknown as SeverityChartDatum)
                .severity,
            ],
          ]),
        "age",
        "severity",
        null,
        false,
        null,
        isForExport
      );

      return () => {
        chart.dispose();
      };
    }
  }, [dashboardData, dataExists, createChart, isForExport]);

  return (
    <Chart
      chartLoading={chartLoading}
      chartName={chartName}
      chartError={dataError}
      chartData={dataExists}
      chartReload={refetchSummary}
    />
  );
};

export default AverageAgeBySeverity;
